import { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { MaskedLines, RevealOnView } from "@/components/Reveal";
import Manifesto from "@/components/Manifesto";
import Team from "@/components/Team";

export default function About() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const goContact = () => navigate("/#contact");

  return (
    <>
      {/* About hero */}
      <section
        id="about-top"
        data-testid="about-hero"
        className="relative min-h-[86vh] w-full overflow-hidden bg-bg flex items-center justify-center"
      >
        <div className="absolute inset-0 blueprint-grid hero-mask pointer-events-none" />

        <div className="relative w-full max-w-[1100px] mx-auto px-6 md:px-12 py-32 md:py-40 text-center">
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            onClick={() => navigate("/")}
            data-testid="about-back-home"
            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.14em] uppercase text-smoke hover:text-forge transition-colors mb-8"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back home
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="eyebrow"
          >
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-forge inline-block" />
              About the firm
            </span>
          </motion.div>

          <h1
            className="font-display text-ink mt-6"
            style={{ fontSize: "clamp(42px, 7vw, 88px)", lineHeight: 1.02, letterSpacing: "-0.02em" }}
          >
            <MaskedLines
              lines={[
                "About",
                <>
                  <span className="text-forge">AxiomForge</span>.
                </>,
              ]}
              delay={0.3}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-8 mx-auto text-ink/75 max-w-[62ch]"
            style={{ fontSize: "18px", lineHeight: 1.55 }}
          >
            Founded in Mumbai, AxiomForge is an engineering consulting firm
            that partners with product teams to design, validate and
            manufacture things that push physics - end-to-end, under one roof.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-3"
          >
            <button
              onClick={goContact}
              data-testid="about-cta-contact"
              className="btn-forge group"
            >
              Start a project
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Manifesto */}
      <Manifesto />

      {/* Team */}
      <Team />

      {/* Closing CTA - light panel with orange corner ticks */}
      <section className="bg-panel text-ink py-20 md:py-28 border-y border-rule relative overflow-hidden">
        <div className="absolute inset-0 blueprint-grid opacity-70 pointer-events-none" />
        <div className="relative max-w-[1200px] mx-auto px-6 md:px-12">
          <span className="tick" style={{ top: 20, left: 20 }} />
          <span className="tick tr" style={{ top: 20, right: 20 }} />
          <span className="tick bl" style={{ bottom: 20, left: 20 }} />
          <span className="tick br" style={{ bottom: 20, right: 20 }} />
          <RevealOnView>
            <div className="grid grid-cols-12 gap-6 items-end">
              <div className="col-span-12 md:col-span-8">
                <div className="eyebrow mb-4">Next</div>
                <h2
                  className="font-display text-ink"
                  style={{ fontSize: "clamp(30px, 4vw, 46px)", lineHeight: 1.05, letterSpacing: "-0.02em" }}
                >
                  Have a hard <span className="text-forge">engineering problem?</span>
                </h2>
              </div>
              <div className="col-span-12 md:col-span-4 flex md:justify-end">
                <button
                  onClick={goContact}
                  data-testid="about-cta-contact-bottom"
                  className="btn-forge group"
                >
                  Start a project
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </div>
            </div>
          </RevealOnView>
        </div>
      </section>
    </>
  );
}
